import { Autocomplete, Card, CardContent, TextField } from '@mui/material'
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  FiEye,
  FiMessageCircle,
  FiX,
  FiEdit,
  FiPlus,
  FiUsers,
  FiDollarSign,
  FiArrowLeft,
} from 'react-icons/fi'
import { RxCrossCircled } from 'react-icons/rx'
import { useNavigate, useParams } from 'react-router-dom'
import api, { membershipApi } from '@/services/api'
import { AddressAutocomplete } from '@/components/AddressAutocomplete'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

type Applicant = {
  id: string
  name: string
  bid: string
  experience: string
  specialization: string
  profileImage?: string
  status: 'Pending' | 'Accepted' | 'Rejected'
  appliedDate: string
  message?: string
}

type Job = {
  id?: string
  title: string
  description: string
  requirements: string
  sessionFrequency: string
  duration: string
  budgetRange: string
  status: 'Active' | 'Closed' | 'Draft'
  applicants: Applicant[]
  createdAt: string
  updatedAt: string
  discipline: string
  modality: string
  goal: string

  // Updated fields with new array structure
  preferredStartDate?: string | Date
  sessionsRequested?: number
  sessionLengthMin?: number
  frequencyPerWeek?: number
  preferredDays?: Array<{ id: string; label: string; selected: boolean }>
  preferredTimeWindows?: Array<{ id: string; label: string; selected: boolean }>
  leadTimeHours?: number

  serviceAddress?: string
  city?: string
  state?: string
  country?: string
  zip?: string
  shareExactAddressWith?: 'nobody' | 'shortlisted' | 'all_matched'
  telehealthState?: string
  latitude?: string
  longitude?: string

  useInsurance?: boolean
  payerName?: string | null
  payerId?: string | null
  memberId?: string | null
  privatePayBudgetPerSession?: number | null
  evvRequired?: boolean
  insuranceId?: string
  insuranceCardId?: string | null

  // Additional fields
  minAge?: number
  maxAge?: number
  targetGender?: string
  languagesPreferred?: Array<{ id: string; label: string; selected: boolean }>
  accessNotes?: string
}

interface InsuranceInfo {
  _id?: string
  payerid?: string
  payer_name?: string
}

interface Insurance {
  _id: string
  patientId: string
  insuranceName: string
  insuranceType: string
  payerId: string
  policyNumber: string
  groupNumber: string
  policyHolder: string
  copay: number
  expiryDate: string
  isDeleted: boolean
  insuranceFrontUrl: string
}

// Updated NewJob type with new array structure
type NewJob = {
  _id?: string
  // Basics
  jobTitle: string
  discipline: 'PT' | 'PTA' | 'OT' | 'OTA' | 'SLP' | ''
  modality: 'in_person' | 'telehealth' | 'hybrid' | ''
  description: string
  goal: string

  // Scheduling with new array structure
  preferredStartDate: string | Date
  sessionsRequested: number | ''
  sessionLengthMin: number | ''
  frequencyPerWeek: number | ''
  preferredDays: Array<{ id: string; label: string; selected: boolean }>
  preferredTimeWindows: Array<{ id: string; label: string; selected: boolean }>
  leadTimeHours: number

  // Location
  serviceAddress: string
  city: string
  state?: string
  zip?: string
  country?: string
  zipCode?: string
  shareExactAddressWith: 'nobody' | 'shortlisted' | 'all_matched'
  telehealthState: string
  latitude?: string
  longitude?: string

  // Languages & Access with new array structure
  languagesPreferred: Array<{ id: string; label: string; selected: boolean }>
  languages?: string[]
  accessNotes: string

  // Billing
  useInsurance: boolean
  useOutofpocket: boolean
  payerName: string
  payerId: string
  memberId: string
  privatePayBudgetPerSession: number | ''
  evvRequired: boolean
  insuranceId?: string | InsuranceInfo | null
  insuranceCardId?: string | null
  selectedInsurance?: Insurance | null

  // Additional fields
  minAge?: number | ''
  maxAge?: number | ''
  targetGender?: string
  status?: string
}

export const getSpecialities = async () => {
  const response = await api.get('specilities/specility')
  return response.data
}

// API service functions
const jobService = {
  // Get all jobs
  getJobs: async (): Promise<Job[]> => {
    const response = await api.get('/jobs')
    return response.data
  },

  // Get job by ID
  getJobById: async (id: string): Promise<Job> => {
    const response = await api.get(`/patient-job/${id}`)
    return response?.data?.data?.data
  },

  // Create new job
  createJob: async (jobData: NewJob): Promise<Job> => {
    const response = await api.post('/patient-job/create', jobData)
    return response.data
  },

  // Update job
  updateJob: async (id: string, jobData: NewJob): Promise<Job> => {
    const response = await api.patch(`/patient-job/${id}`, jobData)
    return response.data
  },

  // Delete job
  deleteJob: async (id: string): Promise<void> => {
    await api.delete(`/jobs/${id}`)
  },

  // Get applicants for a job
  getJobApplicants: async (jobId: string): Promise<Applicant[]> => {
    const response = await api.get(`/jobs/${jobId}/applicants`)
    return response.data
  },

  // Accept applicant
  acceptApplicant: async (
    jobId: string,
    applicantId: string
  ): Promise<void> => {
    await api.post(`/jobs/${jobId}/applicants/${applicantId}/accept`)
  },

  // Reject applicant
  rejectApplicant: async (
    jobId: string,
    applicantId: string
  ): Promise<void> => {
    await api.post(`/jobs/${jobId}/applicants/${applicantId}/reject`)
  },

  // Create contract
  createContract: async (
    jobId: string,
    applicantId: string,
    contractData: any
  ): Promise<void> => {
    await api.post(
      `/jobs/${jobId}/applicants/${applicantId}/contract`,
      contractData
    )
  },
}

const JobsPosting: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams() // For edit mode
  const [jobs, setJobs] = useState<Job[]>([])
  const [showJobModal, setShowJobModal] = useState(false)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [insurances, setInsurances] = useState<Insurance[]>([])
  const [loadingInsurances, setLoadingInsurances] = useState(false)
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(
    null
  )

  // ✅ Transform the full newJob payload to match the target schema without losing any data
  const transformJobPayload = (job: NewJob, patientId?: string) => {
    const dayMap: Record<string, string> = {
      mon: 'Monday',
      tue: 'Tuesday',
      wed: 'Wednesday',
      thu: 'Thursday',
      fri: 'Friday',
      sat: 'Saturday',
      sun: 'Sunday',
    }

    const modalityMap: Record<string, string> = {
      in_person: 'In-Person',
      telehealth: 'Telehealth',
      hybrid: 'Hybrid',
    }

    const addressShareMap: Record<string, string> = {
      nobody: 'Nobody',
      shortlisted: 'Shortlisted Providers',
      all_matched: 'All Matched Providers',
    }

    // ✅ Build a complete, schema-aligned payload
    return {
      // --- Required from the schema ---
      // patientId: patientId || job.patientId || null,

      serviceTitle: job.jobTitle || '',
      specialization: job.discipline || '',
      serviceModality: modalityMap[job.modality] || job.modality || '',

      shortDescription: job.description || '',
      detailedDescription: job.goal || '',

      // --- Languages and preferences ---
      // languages: job.languages || [], // keep this even if empty
      languages:
        job.languagesPreferred
          ?.filter(lang => lang.selected)
          .map(lang => lang.id.toUpperCase()) || [],

      // --- Session details ---
      sessionsRequested: Number(job.sessionsRequested) || 0,
      frequencyPerWeek: Number(job.frequencyPerWeek) || 0,
      sessionLengthMinutes: Number(job.sessionLengthMin) || 0,
      preferredStartDate: job.preferredStartDate,
      preferredDays:
        job.preferredDays
          ?.filter(d => d.selected)
          .map(d => dayMap[d.id] || d.label) || [],
      leadTimeHours: Number(job.leadTimeHours) || 0,
      preferredTimeWindows:
        job.preferredTimeWindows?.filter(t => t.selected).map(t => t.label) ||
        [],

      // --- Location info ---
      serviceAddress: job.serviceAddress || '',
      city: job.city || '',
      country: job.country || '',
      state: job.state || '',
      zipCode: job.zip || '',
      shareExactAddressWith:
        addressShareMap[job.shareExactAddressWith] || 'Nobody',

      accessNotes: job.accessNotes || 'No specific access notes provided.',

      // --- Insurance and billing ---
      useInsurance: job.useInsurance ?? false,
      useOutofpocket: job.useOutofpocket ?? false,
      privatePayBudget:
        job.privatePayBudgetPerSession !== ''
          ? Number(job.privatePayBudgetPerSession)
          : null,
      evvRequired: job.evvRequired ?? true,

      // --- Default system flags ---
      status: job?.status || 'Active',
      // isDeleted: job.isDeleted ?? false,

      // --- Geo and location data ---
      // location: job.location || null,
      latitude: job.latitude || null,
      longitude: job.longitude || null,

      // --- Preserve all original fields for backward compatibility ---
      // originalPayload: job,

      // -- Insurance and Payer info --
      // insuranceId: The insurance card ID from patient's saved insurance
      insuranceId:
        job.selectedInsurance?._id ||
        job.insuranceCardId ||
        (typeof job.insuranceId === 'object'
          ? job.insuranceId?._id
          : job.insuranceId) ||
        null,

      // payerName: The insurance company name
      payerName:
        job.selectedInsurance?.insuranceName ||
        job.payerName ||
        (typeof job.insuranceId === 'object'
          ? job.insuranceId?.payer_name
          : null) ||
        '',

      // payerId: The member ID/policy number (not the insurance company's payer ID)
      member_id:
        job.memberId ||
        job.selectedInsurance?.policyNumber ||
        job.payerId ||
        (typeof job.insuranceId === 'object'
          ? job.insuranceId?.payerid
          : null) ||
        '',

      minAge: job.minAge || '',
      maxAge: job.maxAge || '',
      targetGender: job.targetGender || '',
    }
  }

  // Updated newJob state with new array structure
  const [newJob, setNewJob] = useState<NewJob>({
    // Basics
    jobTitle: '',
    discipline: '',
    modality: 'in_person',
    description: '',
    goal: '',

    // Scheduling with new array structure
    preferredStartDate: '',
    sessionsRequested: '',
    sessionLengthMin: '',
    frequencyPerWeek: '',
    preferredDays: [
      { id: 'mon', label: 'Mon', selected: false },
      { id: 'tue', label: 'Tue', selected: false },
      { id: 'wed', label: 'Wed', selected: false },
      { id: 'thu', label: 'Thu', selected: false },
      { id: 'fri', label: 'Fri', selected: false },
      { id: 'sat', label: 'Sat', selected: false },
      { id: 'sun', label: 'Sun', selected: false },
    ],
    preferredTimeWindows: [
      { id: 'morning', label: 'Morning', selected: false },
      { id: 'afternoon', label: 'Afternoon', selected: false },
      { id: 'evening', label: 'Evening', selected: false },
    ],
    leadTimeHours: 24,

    // Location
    serviceAddress: '',
    city: '',
    country: '',
    state: '',
    zip: '',
    shareExactAddressWith: 'shortlisted',
    telehealthState: '',
    latitude: '',
    longitude: '',

    // Languages with new array structure (will be populated from API)
    languagesPreferred: [],
    accessNotes: '',

    // Billing
    useInsurance: false,
    useOutofpocket: false,
    payerName: '',
    payerId: '',
    memberId: '',
    privatePayBudgetPerSession: '',
    evvRequired: true,
    insuranceId: '',
    insuranceCardId: null,
    selectedInsurance: null,

    // Additional fields
    minAge: '',
    maxAge: '',
    targetGender: '',
  })

  const [showContractModal, setShowContractModal] = useState(false)
  const [selectedApplicant, setSelectedApplicant] = useState<{
    applicant: Applicant
    job: Job
  } | null>(null)
  const [activeTab, setActiveTab] = useState<'Active' | 'Closed' | 'All'>(
    'Active'
  )
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [specialities, setSpecialities] = useState([])
  const [loadingSpecialities, setLoadingSpecialities] = useState(false)
  const [languages, setLanguages] = useState<
    Array<{ key: string; label: string }>
  >([])
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false)
  const [genders, setGenders] = useState<Array<{ key: string; label: string }>>(
    []
  )
  const [isLoadingGenders, setIsLoadingGenders] = useState(false)
  const totalSteps = 5

  // Fetch Specialities, Languages, and Genders on component mount
  useEffect(() => {
    // fetchJobs()
    fetchSpecialities()
    fetchLanguages()
    fetchGenders()
  }, [])

  const fetchLanguages = async () => {
    setIsLoadingLanguages(true)
    try {
      const response = await membershipApi.getLanguages()
      if (response?.success && Array.isArray(response.data)) {
        setLanguages(response.data)
        // Update newJob.languagesPreferred with fetched languages
        setNewJob(prev => ({
          ...prev,
          languagesPreferred: response.data.map(lang => ({
            id: lang.key,
            label: lang.label,
            selected:
              prev.languagesPreferred?.find(l => l.id === lang.key)?.selected ||
              false,
          })),
        }))
      }
    } catch (error) {
      console.error('Error fetching languages:', error)
    } finally {
      setIsLoadingLanguages(false)
    }
  }

  const fetchGenders = async () => {
    setIsLoadingGenders(true)
    try {
      const response = await membershipApi.getGenders()
      if (response?.success && Array.isArray(response.data)) {
        setGenders(response.data)
      }
    } catch (error) {
      console.error('Error fetching genders:', error)
    } finally {
      setIsLoadingGenders(false)
    }
  }

  // Load job data if in edit mode
  useEffect(() => {
    if (id) {
      fetchJobForEdit(id)
    }
  }, [id])

  const fetchSpecialities = async () => {
    setLoadingSpecialities(true)
    try {
      const data = await getSpecialities()
      setSpecialities(data || [])
    } catch (error) {
      console.error('Error fetching specialities:', error)
    } finally {
      setLoadingSpecialities(false)
    }
  }

  // fetchSpecialities();

  // API function to fetch job for editing
  const fetchJobForEdit = async (jobId: string) => {
    try {
      setLoading(true)
      setError(null)

      const res = await api.get(`/patient-job/${jobId}`)
      const job = res?.data?.data

      if (!job) throw new Error('Invalid job data received')

      // Map API days/time/languages to selection arrays
      const dayIds = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
      const dayLabels = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ]

      const timeIds = ['morning', 'afternoon', 'evening']
      const timeLabels = ['Morning', 'Afternoon', 'Evening']

      // const langIds = [
      //   { id: 'en', label: 'English' },
      //   { id: 'es', label: 'Spanish' },
      //   { id: 'fr', label: 'French' },
      //   { id: 'de', label: 'German' },
      //   { id: 'zh', label: 'Chinese' },
      //   { id: 'vi', label: 'Vietnamese' },
      //   { id: 'ko', label: 'Korean' },
      //   { id: 'ru', label: 'Russian' },
      //   { id: 'ar', label: 'Arabic' },
      //   { id: 'pt', label: 'Portuguese' },
      // ]

      const langResponse = await membershipApi.getLanguages()
      const dynamicLanguages = langResponse?.success && Array.isArray(langResponse.data) 
        ? langResponse.data 
        : []

      const editJobData: NewJob = {
        _id: job._id || '',
        jobTitle: job.serviceTitle || '',
        discipline: job.specialization || '',
        modality:
          job.serviceModality === 'In-Person'
            ? 'in_person'
            : job.serviceModality === 'Telehealth'
              ? 'telehealth'
              : job.serviceModality === 'Hybrid'
                ? 'hybrid'
                : '',

        description: job.shortDescription || '',
        goal: job.detailedDescription || '',

        // Scheduling
        preferredStartDate: job.preferredStartDate || '',
        sessionsRequested: job.sessionsRequested || '',
        sessionLengthMin: job.sessionLengthMinutes || '',
        frequencyPerWeek: job.frequencyPerWeek || '',
        preferredDays: dayIds.map((id, index) => ({
          id,
          label: dayLabels[index].slice(0, 3),
          selected: job.preferredDays?.includes(dayLabels[index]) || false,
        })),
        preferredTimeWindows: timeIds.map((id, index) => ({
          id,
          label: timeLabels[index],
          selected:
            job.preferredTimeWindows?.includes(timeLabels[index]) || false,
        })),
        leadTimeHours: job.leadTimeHours || 24,

        // Location
        serviceAddress: job.serviceAddress || '',
        city: job.city || '',
        state: job.state || '',
        country: job.country || '',
        zip: job.zipCode || '',
        latitude: job.latitude,
        longitude: job.longitude,
        shareExactAddressWith:
          job.shareExactAddressWith === 'Nobody'
            ? 'nobody'
            : job.shareExactAddressWith === 'Shortlisted Providers'
              ? 'shortlisted'
              : 'all_matched',
        telehealthState: '',

        // Languages - map language names from API to languagesPreferred
        languagesPreferred: dynamicLanguages.map((lang: { key: string; label: string }) => ({
          id: lang.key,
          label: lang.label,
          selected:
            job.languages?.some(
              (apiLang: string) => apiLang.toUpperCase() === lang.key.toUpperCase()
            ) || false,
        })),
        languages: job.languages || [],
        accessNotes: job.accessNotes || '',

        // Billing
        useInsurance: job.useInsurance ?? false,
        useOutofpocket: job.useOutofpocket ?? false,
        payerName: job?.payerName || (typeof job.insuranceId === 'object' ? job.insuranceId?.insuranceName : '') || '',
        payerId: job?.payerId || (typeof job.insuranceId === 'object' ? job.insuranceId?.payerId : '') || '',
        memberId: job.memberId || (typeof job.insuranceId === 'object' ? job.insuranceId?.policyNumber : '') || '',
        privatePayBudgetPerSession: job?.privatePayBudget || '',
        evvRequired: job.evvRequired ?? true,
        // Extract insurance ID - handle both object and string cases
        insuranceId: typeof job.insuranceId === 'object' && job.insuranceId?._id
          ? job.insuranceId._id
          : typeof job.insuranceId === 'string'
            ? job.insuranceId
            : '',
        // Set insuranceCardId - prefer job.insuranceCardId, otherwise extract from insuranceId object
        insuranceCardId: job.insuranceCardId || (typeof job.insuranceId === 'object' && job.insuranceId?._id ? job.insuranceId._id : null),
        selectedInsurance: null, // Will be set after insurances are loaded

        // Additional
        minAge: job.minAge || '',
        maxAge: job.maxAge || '',
        targetGender: job.targetGender || '',
      }

      setNewJob(editJobData)
    } catch (err: any) {
      console.error('Error fetching job:', err)
      setError(
        'Failed to fetch job: ' + (err.response?.data?.message || err.message)
      )
    } finally {
      setLoading(false)
    }
  }

  // Updated handleArrayToggle for new array structure
  const handleArrayToggle = (
    field: 'preferredDays' | 'preferredTimeWindows' | 'languagesPreferred',
    itemId: string
  ) => {
    setNewJob(prev => ({
      ...prev,
      [field]: prev[field].map(item =>
        item.id === itemId ? { ...item, selected: !item.selected } : item
      ),
    }))
  }

  const handleCreateJob = async (status: 'Active' | 'Draft' = 'Active') => {
    try {
      setLoading(true)
      setError(null)

      // 🔄 Transform the payload before sending
      const transformedPayload = transformJobPayload(newJob)
      transformedPayload.status = status

      if (newJob._id) {
        await jobService.updateJob(newJob._id, transformedPayload as any)
        setSuccess(
          status === 'Draft'
            ? 'Job saved as draft successfully!'
            : 'Job updated successfully!'
        )
      } else {
        await jobService.createJob(transformedPayload as any)
        setSuccess(
          status === 'Draft'
            ? 'Job saved as draft successfully!'
            : 'Job created successfully!'
        )
      }

      setTimeout(() => {
        navigate('/jobs')
      }, 1500)
    } catch (err: any) {
      setError(
        'Failed to save job: ' + (err.response?.data?.message || err.message)
      )
      console.error('Error saving job:', err)
    } finally {
      setLoading(false)
    }
  }

  // const handleViewDetails = (job: Job) => {
  //   navigate(`/jobs/${job.id}`)
  // }

  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepClick = (step: number) => {
    // Allow jumping to any step (similar to ServicePackageStepper)
    if (step >= 1 && step <= totalSteps && step !== currentStep) {
      setCurrentStep(step)
      // Scroll to top when changing steps
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleInputChange = (field: keyof NewJob, value: any) => {
    setNewJob(prev => ({ ...prev, [field]: value }))
  }

  console.log('newJob', newJob)

  // Validation helpers
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1: // Basics
        return (
          newJob.jobTitle.length >= 5 &&
          newJob.discipline !== '' &&
          newJob.modality !== '' &&
          newJob.description.length >= 40
        )
      case 2: // Scheduling
        return newJob.preferredStartDate !== ''
      case 3: // Location
        if (newJob.modality === 'in_person' || newJob.modality === 'hybrid') {
          return (
            newJob.serviceAddress !== '' &&
            newJob.city !== '' &&
            newJob.state !== '' &&
            newJob.country !== '' &&
            newJob.zip !== ''
          )
        }
        if (newJob.modality === 'telehealth') {
          return newJob.telehealthState !== ''
        }
        return true
      case 4: // Languages & Access (optional fields, always valid)
        return true
      case 5: // Billing
        if (newJob.useInsurance && !newJob.useOutofpocket) {
          return (
            newJob.selectedInsurance !== null &&
            newJob.selectedInsurance !== undefined &&
            newJob.memberId !== '' &&
            newJob.memberId.trim() !== ''
          )
        } else if (!newJob.useInsurance && newJob.useOutofpocket) {
          return (
            newJob.privatePayBudgetPerSession !== '' &&
            Number(newJob.privatePayBudgetPerSession) >= 0
          )
        } else if (newJob.useInsurance && newJob.useOutofpocket) {
          return (
            newJob.selectedInsurance !== null &&
            newJob.selectedInsurance !== undefined &&
            newJob.memberId !== '' &&
            newJob.memberId.trim() !== '' &&
            newJob.privatePayBudgetPerSession !== '' &&
            Number(newJob.privatePayBudgetPerSession) >= 0
          )
        } else {
          // Neither checkbox is checked
          return false
        }
      default:
        return true
    }
  }

  // // Reset success and error messages
  // const resetMessages = () => {
  //   setError(null)
  //   setSuccess(null)
  // }

  const fetchPatientInsurances = async () => {
    setLoadingInsurances(true)
    try {
      const response = await api.get(
        '/patient/insurance/list?page=1&limit=100&search='
      )
      setInsurances(response.data.data || [])
    } catch (error) {
      console.error('Error fetching patient insurances:', error)
      setInsurances([])
    } finally {
      setLoadingInsurances(false)
    }
  }

  // Handler for insurance selection
  const handleInsuranceChange = (insurance: Insurance | null) => {
    setSelectedInsurance(insurance)
    if (insurance) {
      setNewJob(prev => ({
        ...prev,
        selectedInsurance: insurance,
        payerName: insurance.insuranceName,
        payerId: insurance.payerId,
        insuranceCardId: insurance._id,
        memberId: insurance.policyNumber,
      }))
    } else {
      setNewJob(prev => ({
        ...prev,
        selectedInsurance: null,
        payerName: '',
        payerId: '',
        insuranceCardId: null,
        memberId: '',
      }))
    }
  }

  // Set selected insurance when editing and insurances are loaded
  useEffect(() => {
    if (insurances.length > 0 && (newJob.insuranceCardId || newJob.insuranceId)) {
      // Try to find insurance by insuranceCardId first, then by insuranceId
      const insuranceIdToMatch = newJob.insuranceCardId || newJob.insuranceId
      const matchingInsurance = insurances.find(
        ins => ins._id === insuranceIdToMatch
      )
      if (matchingInsurance) {
        setSelectedInsurance(matchingInsurance)
        setNewJob(prev => ({
          ...prev,
          selectedInsurance: matchingInsurance,
          payerName: matchingInsurance.insuranceName,
          payerId: matchingInsurance.payerId,
          memberId: matchingInsurance.policyNumber,
          insuranceCardId: matchingInsurance._id,
        }))
      }
    }
  }, [insurances, newJob.insuranceCardId, newJob.insuranceId])

  // Effects
  useEffect(() => {
    fetchPatientInsurances()
  }, [])

  return (
    <div className="p-0 mx-auto max-w-7xl">
      {/* Error and Success Messages */}
      {error && (
        <div className="p-4 mb-4 text-red-700 bg-red-100 border border-red-300 rounded-lg">
          <div className="flex justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 mb-4 text-green-700 bg-green-100 border border-green-300 rounded-lg">
          <div className="flex justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="text-green-700 hover:text-green-900"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/50 z-9999">
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BAE78] mx-auto"></div>
            <p className="mt-4 text-gray-600">Processing...</p>
          </div>
        </div>
      )}

      {/* Create/Edit Job Modal */}
      <div
        onClick={() => navigate('/jobs')}
        className="flex items-center gap-2 px-0 py-0 mx-auto mb-3 max-w-7xl sm:px-6 lg:px-0"
      >
        <button className="flex p-2 text-gray-600 transition-colors hover:text-[#3BAE78] hover:bg-green-50 rounded-2xl">
          <FiArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 text-gray-600 cursor-pointer">Back to Jobs</div>
      </div>

      <div>
        <div className="flex flex-col items-center p-8 text-center">
          {/* Header */}
          <div className="flex flex-col items-center gap-1 mb-4 text-center">
            <h2 className="text-2xl font-semibold text-gray-600">
              {newJob?._id ? 'Edit Job Posting' : 'Create Job Request'}
            </h2>

            <small className="text-gray-600">
              Build a comprehensive service package for your therapy practice.
            </small>
            <small className="text-gray-600">
              Configure clinical coding, pricing, scheduling, and visibility
              settings.
            </small>
          </div>

          {/* Progress Bar */}
          <div className="flex flex-col items-center w-full max-w-4xl mt-4">
            <div className="flex justify-between w-full mb-2">
              {['Basics', 'Scheduling', 'Location', 'Languages', 'Billing'].map(
                (step, index) => {
                  const stepNumber = index + 1
                  const isCompleted = currentStep > stepNumber
                  const isActive = currentStep === stepNumber
                  const isValid = isStepValid(stepNumber)
                  // Show error if step is invalid and has been visited (completed or currently active)
                  const hasError = !isValid && (isCompleted || isActive)

                  return (
                    <div
                      key={step}
                      className="flex flex-col items-center flex-1 cursor-pointer"
                      onClick={() => handleStepClick(stepNumber)}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 relative ${
                          hasError
                            ? 'bg-red-100 border-2 border-red-500 text-red-600 hover:bg-red-200'
                            : isCompleted
                              ? 'bg-[#3BAE78] text-white hover:bg-[#2d8f5f]'
                              : isActive
                                ? 'bg-[#3BAE78] text-white'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {hasError ? (
                          <span className="font-bold text-red-600">!</span>
                        ) : isCompleted ? (
                          '✓'
                        ) : (
                          stepNumber
                        )}
                      </div>
                      <span
                        className={`mt-1 text-xs transition-colors duration-200 ${
                          hasError
                            ? 'text-red-600 font-medium'
                            : isActive || isCompleted
                              ? 'text-[#3BAE78] font-medium'
                              : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                  )
                }
              )}
            </div>

            <div className="w-full h-2 bg-gray-200 rounded-full">
              <div
                className="bg-[#3BAE78] h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="w-full overflow-y-auto bg-white shadow-xl max-w-7xl rounded-2xl">
          <div className="p-6">
            {/* Step 1: Basics */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Basic Information
                </h3>
                <small className="text-gray-600">
                  Provide the core details about your service package. This
                  information will be visible to potential clients.
                </small>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={newJob.jobTitle}
                    onChange={e =>
                      handleInputChange('jobTitle', e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                    placeholder="e.g., Post-op knee rehab at home"
                    minLength={5}
                    maxLength={80}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {newJob.jobTitle.length}/80 characters ({5}-80 required)
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Specialization / Discipline as radio buttons */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Specialization / Discipline *
                    </label>
                    <div className="space-y-3">
                      {loadingSpecialities ? (
                        <p className="text-gray-500">
                          Loading specializations...
                        </p>
                      ) : specialities.length === 0 ? (
                        <p className="text-gray-500">
                          No specializations found.
                        </p>
                      ) : (
                        specialities.map(spec => (
                          <label
                            key={spec._id}
                            className="flex items-center space-x-3 text-gray-700 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="discipline"
                              value={spec.name}
                              checked={newJob.discipline === spec.name}
                              onChange={e =>
                                handleInputChange('discipline', e.target.value)
                              }
                              className="w-5 h-5 text-[#3BAE78] border-gray-300 focus:ring-[#3BAE78] accent-[#3BAE78] cursor-pointer"
                            />
                            <span className="text-base">{spec.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Modality as disabled checkbox */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Modality *
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        className="w-5 h-5 text-[#3BAE78] border-gray-300 accent-[#3BAE78] cursor-not-allowed"
                      />
                      <span className="text-base text-gray-700">In Person</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Short Description *
                  </label>
                  <textarea
                    value={newJob.description}
                    onChange={e =>
                      handleInputChange('description', e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                    rows={4}
                    placeholder="Describe your therapy needs and goals in plain language..."
                    minLength={40}
                    maxLength={300}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Brief overview of your service {newJob.description.length}
                    /300 characters ({40}-300 required)
                  </p>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Detailed Description (Optional)
                  </label>
                  <textarea
                    value={newJob.goal}
                    onChange={e => handleInputChange('goal', e.target.value)}
                    className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                    rows={3}
                    placeholder="Any additional goals or specific outcomes you'd like to achieve..."
                    maxLength={5000}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Comprehensive description of your service (
                    {newJob.goal.length}/5000 characters)
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-gray-900">
                  Additional Information for Target Provider
                </h3>

                <div className="flex flex-col w-full gap-4 md:flex-row">
                  {/* Age Range */}
                  {/* <div className="flex-1 w-full">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Age Range (Optional)
                    </label>
                    <div className="flex w-full gap-2">
                      <input
                        type="number"
                        value={newJob.minAge}
                        onChange={e =>
                          handleInputChange('minAge', e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                        placeholder="Min e.g., 18 Years"
                      />

                      <input
                        type="number"
                        value={newJob.maxAge}
                        onChange={e =>
                          handleInputChange('maxAge', e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                        placeholder="Max e.g., 65 Years"
                      />
                    </div>
                  </div> */}

                  {/* Target Gender */}
                  <div className="flex-1 w-full">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Target Gender
                    </label>
                    <select
                      value={newJob.targetGender}
                      onChange={e =>
                        handleInputChange('targetGender', e.target.value)
                      }
                      disabled={isLoadingGenders}
                      className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {isLoadingGenders
                          ? 'Loading...'
                          : 'Select Target Gender'}
                      </option>
                      {genders.map(gender => (
                        <option key={gender.key} value={gender.label}>
                          {gender.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Scheduling */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Scheduling Preferences
                </h3>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Preferred Start Date *
                    </label>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={
                          newJob.preferredStartDate
                            ? new Date(newJob.preferredStartDate)
                            : null
                        }
                        minDate={new Date()}
                        onChange={newValue => {
                          if (newValue) {
                            // Format as YYYY-MM-DD for payload
                            const formatted = newValue
                              .toISOString()
                              .split('T')[0]
                            handleInputChange('preferredStartDate', formatted)
                          } else {
                            handleInputChange('preferredStartDate', '')
                          }
                        }}
                        format="MM/dd/yyyy"
                        slotProps={{
                          textField: {
                            placeholder: 'MM-DD-YYYY',
                            fullWidth: true,
                            sx: {
                              '& .css-11blmcf-MuiPickersInputBase-root-MuiPickersOutlinedInput-root':
                                {
                                  padding: '0 14px',
                                  borderRadius: '16px',
                                  height: '52px',
                                },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '16px',
                                height: '52px',
                                '& fieldset': {
                                  borderColor: '#D1D5DB',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#A1A1AA',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3BAE78',
                                  borderWidth: '2px',
                                },
                              },
                              '& input': {
                                padding: 0,
                                fontSize: '0.95rem',
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Sessions Requested
                    </label>
                    <input
                      type="number"
                      value={newJob.sessionsRequested}
                      onChange={e =>
                        handleInputChange(
                          'sessionsRequested',
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                      className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                      placeholder="1-60"
                      min="1"
                      max="60"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Session Length (minutes)
                    </label>
                    <input
                      type="number"
                      value={newJob.sessionLengthMin}
                      onChange={e =>
                        handleInputChange(
                          'sessionLengthMin',
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                      className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                      placeholder="15-120"
                      min="15"
                      max="120"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Frequency (per week)
                    </label>
                    <input
                      type="number"
                      value={newJob.frequencyPerWeek}
                      onChange={e =>
                        handleInputChange(
                          'frequencyPerWeek',
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                      className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                      placeholder="1-7"
                      min="1"
                      max="7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Preferred Days
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {newJob.preferredDays.map(day => (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() =>
                          handleArrayToggle('preferredDays', day.id)
                        }
                        className={`px-3 py-3 rounded-2xl border text-sm ${
                          day.selected
                            ? 'bg-[#3BAE78] text-white border-[#3BAE78]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Preferred Time Windows
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {newJob.preferredTimeWindows.map(time => (
                      <button
                        key={time.id}
                        type="button"
                        onClick={() =>
                          handleArrayToggle('preferredTimeWindows', time.id)
                        }
                        className={`px-3 py-3 rounded-2xl border text-sm capitalize ${
                          time.selected
                            ? 'bg-[#3BAE78] text-white border-[#3BAE78]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        {time.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Lead Time (hours before first session)
                  </label>
                  <input
                    type="number"
                    value={
                      newJob.leadTimeHours === 0
                        ? ''
                        : newJob.leadTimeHours
                    }
                    onChange={e => {
                      const raw = e.target.value
                      if (raw === '') {
                        handleInputChange('leadTimeHours', 0)
                        return
                      }
                      const num = parseInt(raw, 10)
                      if (!Number.isNaN(num) && num >= 0) {
                        handleInputChange('leadTimeHours', num)
                      }
                    }}
                    className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                    min="1"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Location */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Location Details
                </h3>

                {(newJob.modality === 'in_person' ||
                  newJob.modality === 'hybrid') && (
                  <>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Service Address *
                      </label>
                      <AddressAutocomplete
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '& fieldset': { borderColor: 'grey.300' },
                            '&:hover fieldset': {
                              borderColor: 'grey.400',
                              transform: 'scale(1.02)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3BAE78',
                              borderWidth: 2,
                            },
                          },
                          '& .MuiInputBase-input': {
                            py: 1.5,
                            fontSize: '0.875rem',
                          },
                        }}
                        value={newJob.serviceAddress}
                        onChange={(address, components) => {
                          // Update main address
                          handleInputChange('serviceAddress', address)

                          // Update dependent fields dynamically
                          if (components) {
                            if (components.city)
                              handleInputChange('city', components.city)
                            if (components.state)
                              handleInputChange('state', components.state)
                            if (components.country)
                              handleInputChange('country', components.country)
                            if (components.zipCode || components.postalCode)
                              handleInputChange(
                                'zip',
                                components.zipCode || components.postalCode
                              )
                            if (components.latitude !== undefined)
                              handleInputChange('latitude', components.latitude)
                            if (components.longitude !== undefined)
                              handleInputChange(
                                'longitude',
                                components.longitude
                              )
                          }
                        }}
                        placeholder="Start typing your address..."
                        addressType="address"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          City *
                        </label>
                        <input
                          type="text"
                          value={newJob.city}
                          onChange={e =>
                            handleInputChange('city', e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                          placeholder="e.g., Houston"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          State *
                        </label>
                        <input
                          type="text"
                          value={newJob?.state}
                          onChange={e =>
                            handleInputChange('state', e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                          placeholder="e.g., Texas"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          ZIP Code *
                        </label>
                        <input
                          type="text"
                          value={newJob?.zip}
                          onChange={e =>
                            handleInputChange('zip', e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                          placeholder="5 digits"
                          maxLength={5}
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Country *
                        </label>
                        <input
                          type="text"
                          value={newJob?.country}
                          onChange={e =>
                            handleInputChange('country', e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                          placeholder="e.g., United States"
                        />
                      </div>
                    </div>
                  </>
                )}

                {(newJob.modality === 'telehealth' ||
                  newJob.modality === 'hybrid') && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Telehealth State (your state of residence) *
                    </label>
                    <input
                      type="text"
                      value={newJob.telehealthState}
                      onChange={e =>
                        handleInputChange('telehealthState', e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                      placeholder="2-letter code"
                      maxLength={2}
                    />
                  </div>
                )}

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Share Exact Address With
                  </label>
                  <select
                    value={newJob.shareExactAddressWith}
                    onChange={e =>
                      handleInputChange('shareExactAddressWith', e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                  >
                    <option value="nobody">Nobody (coordinates only)</option>
                    <option value="shortlisted">Shortlisted Providers</option>
                    <option value="all_matched">All Matched Providers</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 4: Languages & Access */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Languages & Access
                </h3>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Preferred Languages
                  </label>
                  {isLoadingLanguages ? (
                    <div className="py-4 text-sm text-center text-gray-500">
                      Loading languages...
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {newJob.languagesPreferred.length === 0 ? (
                        <div className="text-sm text-gray-500">
                          No languages available. Please refresh the page.
                        </div>
                      ) : (
                        newJob.languagesPreferred.map(lang => (
                          <button
                            key={lang.id}
                            type="button"
                            onClick={() =>
                              handleArrayToggle('languagesPreferred', lang.id)
                            }
                            className={`px-3 py-3 rounded-2xl border text-sm ${
                              lang.selected
                                ? 'bg-[#3BAE78] text-white border-[#3BAE78]'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {lang.label}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Access Notes
                  </label>
                  <textarea
                    value={newJob.accessNotes}
                    onChange={e =>
                      handleInputChange('accessNotes', e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                    rows={3}
                    placeholder="Stairs, parking, pets, caregiver present, etc."
                    maxLength={400}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {newJob.accessNotes.length}/400 characters
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Billing */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Billing Information
                </h3>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="useInsurance"
                    checked={newJob.useInsurance}
                    onChange={e =>
                      handleInputChange('useInsurance', e.target.checked)
                    }
                    className="w-4 h-4 text-[#3BAE78] border-gray-300 rounded focus:ring-[#3BAE78]"
                  />
                  <label
                    htmlFor="useInsurance"
                    className="text-sm font-medium text-gray-700"
                  >
                    Use Insurance
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="useOutofpocket"
                    checked={newJob.useOutofpocket}
                    onChange={e =>
                      handleInputChange('useOutofpocket', e.target.checked)
                    }
                    className="w-4 h-4 text-[#3BAE78] border-gray-300 rounded focus:ring-[#3BAE78]"
                  />
                  <label
                    htmlFor="useOutofpocket"
                    className="text-sm font-medium text-gray-700"
                  >
                    Out-of-Pocket
                  </label>
                </div>

                {newJob.useInsurance && (
                  <>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Select Saved Insurance *
                        </label>
                        <Autocomplete
                          size="small"
                          options={insurances}
                          getOptionLabel={option => option.insuranceName}
                          value={selectedInsurance}
                          onChange={(_, newValue) =>
                            handleInsuranceChange(newValue)
                          }
                          loading={loadingInsurances}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder="Choose from saved insurance cards"
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '1rem',
                                  '& fieldset': {
                                    borderColor: '#d1d5db',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#a1a1aa',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#3BAE78',
                                    borderWidth: '2px',
                                  },
                                },
                              }}
                            />
                          )}
                          slotProps={{
                            popper: {
                              sx: {
                                zIndex: 9999,
                              },
                            },
                          }}
                        />
                        {insurances.length === 0 && !loadingInsurances && (
                          <p className="mt-1 text-xs text-gray-500">
                            No saved insurance cards found. Please add an
                            insurance card in your account settings.
                          </p>
                        )}
                      </div>

                      {selectedInsurance && (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Member ID *
                          </label>
                          <input
                            type="text"
                            value={newJob.memberId}
                            onChange={e =>
                              handleInputChange('memberId', e.target.value)
                            }
                            readOnly
                            className="w-full border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                            placeholder="Member ID"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {newJob.useOutofpocket && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Private Pay Budget Per Session *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <FiDollarSign className="w-5 h-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        value={newJob.privatePayBudgetPerSession}
                        onChange={e =>
                          handleInputChange(
                            'privatePayBudgetPerSession',
                            e.target.value === '' ? '' : Number(e.target.value)
                          )
                        }
                        className="w-full pl-10 border border-gray-300 rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#3BAE78] focus:border-transparent"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="evvRequired"
                    checked={newJob.evvRequired}
                    disabled
                    onChange={e =>
                      handleInputChange('evvRequired', e.target.checked)
                    }
                    className="w-4 h-4 text-[#3BAE78] border-gray-300 rounded focus:ring-[#3BAE78]"
                  />
                  <label
                    htmlFor="evvRequired"
                    className="text-sm font-medium text-gray-700"
                  >
                    EVV Required (Electronic Visit Verification for in-home
                    services)
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between p-6 border-t">
            <button
              className="px-4 py-3 text-gray-700 transition-colors border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
              onClick={
                currentStep === 1 ? () => navigate('/jobs') : handlePrevStep
              }
              disabled={loading}
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>

            <div className="flex gap-3">
              {currentStep < totalSteps ? (
                <>
                  <button
                    className="px-4 py-3 text-gray-700 transition-colors border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 disabled:opacity-50"
                    onClick={() => handleCreateJob('Draft')}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save as Draft'}
                  </button>
                  <button
                    className="px-4 py-3 bg-[#3BAE78] text-white rounded-lg hover:bg-green-800 transition-colors disabled:opacity-50 cursor-pointer"
                    onClick={handleNextStep}
                    disabled={!isStepValid(currentStep) || loading}
                  >
                    {loading ? 'Processing...' : 'Next'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="px-4 py-3 text-gray-700 transition-colors border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 disabled:opacity-50"
                    onClick={() => handleCreateJob('Draft')}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save as Draft'}
                  </button>
                  <button
                    className="px-4 py-3 bg-[#3BAE78] text-white rounded-lg hover:bg-green-800 transition-colors disabled:opacity-50 cursor-pointer"
                    onClick={() => handleCreateJob('Active')}
                    disabled={!isStepValid(currentStep) || loading}
                  >
                    {loading
                      ? 'Creating...'
                      : newJob._id
                        ? 'Update Job Request'
                        : 'Create Job Request'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JobsPosting
